import type { APP_CONSTANTS } from '~/common/explicit-exports';

type ContentSourceType =
  (typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES)[keyof typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES];

export interface LearnerSearchResultItem {
  id: string;
  type: string;
  contentSource?: ContentSourceType;
  resultTitle: string;
  textValue: string;
  resultSubtitle?: string;
  lastUpdated: string;
}

interface GenericAlgoliaHit {
  id: string;
  type: string;
  name: string;
  description: string;
  updated_at: number;
  last_updated_by_name: string;
}

export interface GuideSectionAlgoliaHit extends GenericAlgoliaHit {
  type: 'guide_section';
  guide_id: string;
  guide_title: string;
}

export interface GuideTaskAlgoliaHit extends GenericAlgoliaHit {
  type: 'guide_task';
  guide_id: string;
  guide_title: string;
  section_id: string;
  section_title: string;
}

export type AlgoliaHit = GenericAlgoliaHit | GuideSectionAlgoliaHit | GuideTaskAlgoliaHit;

export type DocumentObjectTypes =
  | 'guide_task'
  | 'guide_section'
  | 'guide'
  | 'challenge'
  | 'resource'
  | 'scorm'
  | 'content_provider_content'
  | 'training_series'
  | 'university'
  | 'cms_file'
  | 'cms_link'
  | 'path'
  | 'section';

export type Guide = {
  id: string;
  name: string;
  description?: string;
  ui_order: number;
  updated_by: {
    id: string;
    name: string;
  };
  updated_at: number;
};
type GuideWithSection = Guide & {
  section: {
    id: string;
    name: string;
    ui_order: number;
  };
};

type BaseSearchResultItem = {
  id: string;
  name: string;
  description?: string;
  updatedDate: number;
  updatedBy: {
    id: string;
    name: string;
  };
  snippetMatch?: {
    title: string;
    subtitle: string;
    matchedWords: Array<string>;
  };
  contentSource?: null;
};

export type SearchResultItemSection = BaseSearchResultItem & {
  type: 'guide_section';
  guide: Guide;
};
export type SearchResultItemTask = BaseSearchResultItem & {
  type: 'guide_task';
  guide: GuideWithSection;
};
export type SearchResultItemContentProviderContent = BaseSearchResultItem & {
  type: 'content_provider_content';
  contentSource:
    | (typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES)['LINKEDIN_LEARNING']
    | (typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES)['UDEMY'];
};

export type SearchResultItem =
  | (BaseSearchResultItem & {
      type: Exclude<DocumentObjectTypes, 'guide_section' | 'guide_task'>;
    })
  | SearchResultItemSection
  | SearchResultItemTask
  | SearchResultItemContentProviderContent;

export type GroupSearchResultItem = BaseSearchResultItem & {
  type: Exclude<DocumentObjectTypes, 'guide_section' | 'guide_task'>;
  children: Array<SearchResultItemSection | SearchResultItemTask>;
};

export enum UpdateDate {
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
  LAST_YEAR = 'last_year',
  ALL_TIME = 'all_time',
}

export type SearchResultContentType =
  | 'guide'
  | 'path'
  | 'library'
  | 'resource'
  | 'challenge'
  | 'scorm'
  | 'cms_link'
  | 'cms_file'
  | 'event'
  | 'content_provider_content';

export type SearchResultContentSource = 'linkedin_learning' | 'udemy';

export type SortBy = 'recent' | 'relevance';
export type OrderBy = 'asc' | 'desc';
export type HighlightOption = 'strong' | 'background';

export type SearchQueryParams = {
  query: string;
  types?: SearchResultContentType[];
  contentSources?: SearchResultContentSource[];
  tags?: string[];
  skills?: string[];
  updatedDate?: UpdateDate;
  updatedBy?: string[];
  offset?: number;
  highlightOption?: HighlightOption;
};

export type SearchQueryResponse = {
  data: SearchResultItem[];
  total: number;
  offset: number;
};
